/**
 * Emit hosts for the `documents` analytics group — 12 functions
 * lifted out of `analyticsScribeModel`'s effects so a call site can emit
 * without holding a Rematch `dispatch`. Every payload is the effect's own.
 */
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  type DocumentEventProps,
  type DocumentExportContext,
  type DocumentTemplateQueueLimitReachedEventProps,
  type DocumentTemplateQueueReorderedEventProps,
  type QueuedDocumentGenerationCancelledEventProps,
  type QueuedDocumentGenerationFailedEventProps,
  type QueuedDocumentGenerationStartedEventProps,
  type QueuedDocumentTabTemplateSwappedEventProps,
  type SessionStartedWithDocumentQueueEventProps,
  type SettingsPipeline,
} from '@/models/analytics/features/documents';
import { type CopySettingsSnapshot } from '@/models/analytics/features/sessions';
import { documentExported } from '@heidi/session/data';

export function documentPrintedV2({
  sessionId,
  documentId,
  export_context,
  is_pinned_template,
  is_starter_pack_template,
  settingsPipeline,
  copySettings,
}: {
  sessionId: string;
  documentId?: string;
  export_context?: DocumentExportContext;
  is_pinned_template: boolean;
  is_starter_pack_template: boolean;
  settingsPipeline?: SettingsPipeline;
  copySettings?: CopySettingsSnapshot;
}) {
  documentExported({
    session_id: sessionId,
    document_id: documentId ?? '',
    format: 'print',
    export_context,
    is_pinned_template,
    is_starter_pack_template,
    settings_pipeline: settingsPipeline,
    copy_settings: copySettings,
  });
}

/**
 * Track document generated event
 * Snowplow tracking is handled centrally in trackEventV2
 * @param props - Document event properties
 */
export function trackDocumentGenerated(props: DocumentEventProps) {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.DocumentGenerated,
    props,
  });
}

// Queued document templates
export function trackDocumentTemplateQueueLimitReached(
  input: DocumentTemplateQueueLimitReachedEventProps
) {
  track({
    event_name: TrackingEventName.DocumentTemplateQueueLimitReached,
    props: input,
  });
}

export function trackDocumentTemplateQueueReordered(
  input: DocumentTemplateQueueReorderedEventProps
) {
  track({
    event_name: TrackingEventName.DocumentTemplateQueueReordered,
    props: input,
  });
}

export function trackQueuedDocumentGenerationCancelled(
  input: QueuedDocumentGenerationCancelledEventProps
) {
  track({
    event_name: TrackingEventName.QueuedDocumentGenerationCancelled,
    props: input,
  });
}

export function trackQueuedDocumentGenerationFailed(
  input: QueuedDocumentGenerationFailedEventProps
) {
  track({
    event_name: TrackingEventName.QueuedDocumentGenerationFailed,
    props: input,
  });
}

export function trackQueuedDocumentGenerationStarted(
  input: QueuedDocumentGenerationStartedEventProps
) {
  track({
    event_name: TrackingEventName.QueuedDocumentGenerationStarted,
    props: input,
  });
}

export function trackQueuedDocumentTabTemplateSwapped(
  input: QueuedDocumentTabTemplateSwappedEventProps
) {
  track({
    event_name: TrackingEventName.QueuedDocumentTabTemplateSwapped,
    props: input,
  });
}

export function trackSessionStartedWithDocumentQueue(
  input: SessionStartedWithDocumentQueueEventProps
) {
  track({
    event_name: TrackingEventName.SessionStartedWithDocumentQueue,
    props: input,
  });
}
